import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Users } from 'lucide-react';
import CTA from '../../components/CTA';

const LurganWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Management Lurgan | Emotional Eating Hypnotherapy County Armagh</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Lurgan from Belfast clinic (27 miles). Address emotional eating, working-class stress eating patterns, sustainable behavior change. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Lurgan, emotional eating Lurgan, weight management County Armagh, stress eating Lurgan, mindful eating Lurgan" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/lurgan-weight-management/" />
        <meta property="og:title" content="Weight Management Lurgan | Emotional Eating Hypnotherapy County Armagh" />
        <meta property="og:description" content="Weight management hypnotherapy serving Lurgan. Address emotional eating, stress eating patterns, sustainable lifestyle change." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Lurgan",
            "description": "CNHC registered weight management hypnotherapy serving Lurgan from Belfast clinic. Specializing in emotional eating, stress eating patterns, and sustainable behavior change without diet culture.",
            "url": "https://www.sharonclare-hypnotherapy.com/lurgan-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Lurgan, Northern Ireland",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy in Lurgan
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Address emotional eating and working-class stress eating patterns with compassionate, evidence-based support
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">27 miles</span> from Lurgan via M1 • <span className="font-semibold">35-40 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for Lurgan residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management for Lurgan
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Lurgan community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, located approximately 27 miles north via the M1 motorway. The journey takes just 35-40 minutes from Junction 10, providing accessible weight management support at a manageable distance that makes regular in-person sessions entirely practical for residents of this historic market town.
              </p>
              <p>
                Weight struggles in Lurgan often connect to the unique pressures facing working-class communities. Economic pressures, job insecurity, shift work patterns, and the stress of managing household budgets all create fertile ground for emotional eating. If you find yourself reaching for comfort foods when money worries surface, eating to cope with work stress, or turning to food as one of the few affordable pleasures in a financially constrained life, you're experiencing emotional eating - and this isn't about willpower or moral failure.
              </p>
              <p>
                Solution-focused hypnotherapy for weight management works differently from diets. Instead of focusing on restriction, calorie counting, and food rules that eventually fail, we address why you eat when you're not physically hungry. By calming your stress response, building emotional resilience, and changing your relationship with food at a subconscious level, you can achieve sustainable behavior change without the cycle of deprivation and rebound that characterizes most diets. My approach is compassionate, non-judgmental, and focused on long-term lifestyle change rather than unrealistic quick fixes or diet culture promises.
              </p>
              <p>
                Many Lurgan residents work in retail, manufacturing, or service sector jobs, facing customer-facing stress, physically demanding labor, and irregular schedules that disrupt eating patterns. The challenge of maintaining healthy eating while working long hours, managing tight budgets, and dealing with the fatigue that comes from economic pressure creates patterns that feel impossible to break. You might grab convenience food because you're too exhausted to cook after a long shift, eat quickly standing up because there's no time to sit down, or use food as a reward after dealing with difficult customers or demanding work. These patterns aren't personal failures - they're understandable responses to the pressures of working-class life.
              </p>
              <p>
                Hypnotherapy helps you develop healthier coping strategies that don't involve food. By addressing the stress at its root - whether that's work pressure, economic anxiety, or the accumulated burden of managing life on a tight budget - we can break the automatic connection between difficult emotions and eating. When your nervous system learns to calm down, when you have better tools for managing stress, food loses its power as an emotional crutch. The changes happen at a subconscious level, so they feel natural rather than forced, creating sustainable weight management without the deprivation mentality of dieting.
              </p>
              <p>
                For Lurgan clients unable to make the 35-40 minute drive to Belfast regularly, or who simply prefer the convenience of remote sessions, I also offer secure online hypnotherapy via video call. Online sessions provide the same professional, evidence-based treatment with the flexibility to attend from your home. This option has been particularly valuable for clients with caring responsibilities, shift work schedules, or those who want to integrate therapy into busy lives without adding travel time. The therapeutic effectiveness is equivalent to in-person sessions while offering maximum convenience and eliminating travel costs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Emotional Eating */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Understanding Emotional Eating in Lurgan
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As someone who has worked extensively with Northern Ireland working-class communities, I understand the specific stressors that contribute to emotional eating patterns in Lurgan. Economic pressure is real and constant - worrying about bills, managing on tight budgets, the stress of job insecurity or zero-hours contracts. When you're dealing with financial anxiety day after day, food becomes one of the few accessible forms of comfort and pleasure. A cheap bag of crisps or a chocolate bar provides a brief moment of relief from the grinding stress of making ends meet.
              </p>
              <p>
                For those working in retail, service roles, or manufacturing in Lurgan, different but equally challenging patterns emerge. Customer-facing stress triggers comfort food cravings during breaks - that biscuit tin in the staff room becomes your stress relief. Physically demanding work leads to fatigue eating - you're genuinely tired, and your brain craves quick energy from sugar and carbs. Irregular shift patterns disrupt normal meal times, leading to grazing, skipped meals followed by binge eating, and dependence on convenience foods because cooking proper meals around shift work feels impossible.
              </p>
              <p>
                The working-class food environment in Lurgan also plays a role. When fresh, healthy food costs more than processed convenience food, when you're tired from work and fast food is cheap and quick, when cooking from scratch requires time and energy you simply don't have after a demanding shift - these aren't personal failures. These are structural challenges that make emotional eating and weight gain almost inevitable without addressing the underlying stress and developing practical coping strategies that work within the constraints of your actual life.
              </p>
              <p>
                What all these patterns have in common is that they're driven by stress and difficult emotions, not by physical hunger. Your subconscious mind has learned that food provides quick relief from worry, exhaustion, frustration, or the grinding stress of economic pressure. The problem is that emotional eating creates its own cycle: stress leads to eating, which leads to weight gain and guilt, which leads to more stress and shame, which leads to more eating. Breaking this cycle requires addressing the stress at its root, not just trying to "eat less and move more."
              </p>
              <p>
                Hypnotherapy interrupts this cycle by teaching your nervous system to calm down, reducing the baseline stress that drives emotional eating. When you have healthier ways to manage difficult emotions - practical stress management techniques that work around shift patterns, better sleep despite irregular hours, emotional resilience that doesn't depend on food - eating loses its power as your primary coping mechanism. When your subconscious beliefs about food, eating, and your body shift from shame and restriction to compassion and balance, sustainable behavior change becomes possible even when external circumstances remain challenging.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How Hypnotherapy Helps with Weight Management
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Addresses Emotional Eating
                </h3>
                <p className="text-gray-700">
                  When stress, anxiety, or difficult emotions trigger eating, it's your subconscious mind running old patterns. Hypnotherapy accesses that subconscious level, breaking the connection between emotions and eating for comfort. You learn healthier ways to manage economic stress, work pressure, and daily challenges that don't involve food, creating lasting change rather than temporary willpower.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Calms Stress Response
                </h3>
                <p className="text-gray-700">
                  When your body is stuck in stress mode from job insecurity, financial worries, or shift work fatigue, hunger hormones increase, cravings intensify, and your brain becomes hyper-focused on high-calorie foods. By teaching your nervous system to switch off the stress response, hypnotherapy naturally reduces cravings and emotional eating urges without requiring iron willpower.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Users className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Sustainable Behavior Change
                </h3>
                <p className="text-gray-700">
                  Diets work short-term but fail long-term because they rely on willpower and restriction. Hypnotherapy creates lasting change by addressing the root causes of overeating - economic stress, work pressure, emotional patterns, and subconscious beliefs about food. The changes feel natural, not forced, making sustainable weight management genuinely achievable within the constraints of your real life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              My Approach to Weight Management
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 mb-4">
                I don't make promises about weight loss numbers, and I don't provide meal plans or diet advice. My focus is entirely on the psychological and emotional aspects of eating. You already know what healthy eating looks like - the problem isn't lack of information, it's that stress, economic pressure, and subconscious patterns override that knowledge.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Instead of telling you what to eat or promising you'll lose a specific amount of weight, I work with you to understand why you eat when you're not physically hungry. What emotions trigger overeating? What daily stressors send you reaching for comfort food? What subconscious beliefs about food, money, and self-worth are keeping you stuck? When we address these underlying drivers, your relationship with food changes naturally.
              </p>
              <p className="text-lg text-gray-700">
                This approach is particularly important for working-class clients in Lurgan because I understand that your challenges aren't just about "willpower" - they're about managing real economic stress, working demanding jobs, juggling multiple responsibilities on limited resources, and dealing with the accumulated burden of financial pressure. My approach is practical, compassionate, and focused on changes that work within your actual life circumstances rather than requiring you to somehow transform into someone with unlimited time and money.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                What to Expect from Sessions
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Initial Consultation</h4>
                  <p className="text-gray-700">
                    We'll explore your weight history, eating patterns, stress triggers, work environment, and goals. I'll explain how economic stress and work pressure drive eating behaviors, and how hypnotherapy works at a neurological level to create change. You'll also experience your first hypnotherapy session, leaving feeling calm and motivated.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ongoing Sessions</h4>
                  <p className="text-gray-700">
                    Most clients benefit from a package of 3 sessions, typically weekly or fortnightly. Each session builds on the last, gradually reprogramming your subconscious patterns around food, stress, and emotional eating. You'll notice changes fairly quickly - reduced cravings, better awareness of hunger and fullness, more control around trigger foods, improved sleep (which dramatically affects weight), and a calmer relationship with food and your body.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Session Options for Lurgan
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Single Session
                </h3>
                <p className="text-gray-700 mb-4">
                  One comprehensive weight management hypnotherapy session addressing your emotional eating patterns and stress triggers.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Full assessment and consultation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Hypnotherapy session</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Take-home audio recording</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 border-2 border-accent-600">
                <div className="bg-accent-600 text-white text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">
                  MOST POPULAR
                </div>
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  3-Session Package
                </h3>
                <p className="text-gray-700 mb-4">
                  Comprehensive weight management programme for sustainable behavioral change and lasting results.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Three full hypnotherapy sessions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Progressive subconscious reprogramming</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Audio recordings for reinforcement</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Email support between sessions</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-xl p-6 border border-gray-200">
              <p className="text-gray-700 text-center">
                For working families in Lurgan, I understand affordability matters. Transparent pricing means no hidden costs or pressure to buy additional packages. Many clients find the 3-session package provides the best value for creating lasting change in eating patterns and stress management.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Lurgan
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Recommended)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M1 motorway northbound from Junction 10 (near Lurgan) into Belfast, then south along the Ormeau Road
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> 27 miles through Moira and Lisburn
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 35-40 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Practical and Manageable</h3>
                <p className="text-gray-700">
                  At 27 miles via the M1 motorway, the journey from Lurgan to Belfast is genuinely practical for regular hypnotherapy appointments. The M1 from Junction 10 is a high-quality dual carriageway that makes for a straightforward drive. The 35-40 minute journey is a reasonable investment for professional weight management support that addresses the root causes of emotional eating rather than just providing another temporary diet.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer not to drive to Belfast? I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional treatment with the convenience of attending from your home in Lurgan, eliminating travel time and costs. This option is ideal for those with mobility concerns, caring responsibilities, shift work patterns, or anyone who prefers the flexibility of online therapy. Many clients combine in-person and online sessions based on their schedule and circumstances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What County Armagh Clients Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in retail, I was comfort eating every break to cope with customer stress and being on my feet all day. Sharon helped me understand I wasn't actually hungry - I was using food to manage difficult emotions. After the 3-session package, my eating patterns have completely changed. I'm more aware of when I'm genuinely hungry versus eating from stress, and I've developed better ways to decompress that don't involve the staff room biscuit tin."
                </p>
                <p className="font-semibold text-gray-900">K.R., County Armagh - Stress Eating</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Money worries were driving my eating - cheap comfort food was one of the few pleasures I could afford when everything felt tight financially. Sharon's approach isn't about willpower or another diet to fail at. It's about understanding why you reach for food when you're stressed or worried. The hypnotherapy helped me break that automatic connection between financial anxiety and eating. I'm managing my stress better and not using food as my only coping strategy anymore."
                </p>
                <p className="font-semibold text-gray-900">M.D., Craigavon Borough - Emotional Eating</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Shift work meant my eating patterns were all over the place - grabbing food whenever I could, eating standing up, relying on convenience food because I was too tired to cook. Online sessions from home worked perfectly around my irregular hours. Sharon helped me develop a healthier relationship with food despite the challenges of shift work. The changes feel sustainable this time, not like the restrictive diets I'd tried before that always failed within a few weeks."
                </p>
                <p className="font-semibold text-gray-900">L.T., County Armagh - Shift Work Eating Patterns</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Lurgan Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Lurgan from the Belfast clinic?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 27 miles from Lurgan via the M1 motorway from Junction 10. The journey typically takes 35-40 minutes by car in normal traffic conditions. The M1 is a high-quality dual carriageway that makes for a straightforward drive. At this distance, regular hypnotherapy appointments are genuinely practical - it's a reasonable journey for specialist support that addresses the root causes of emotional eating rather than just providing another temporary diet that will fail.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can weight management hypnotherapy be done online?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Online hypnotherapy sessions via video call are just as effective as in-person sessions. The hypnotherapy process works equally well through a screen, and many Lurgan clients find online sessions more convenient - no 35-40 minute drive to Belfast, easier to schedule around shift work or caring responsibilities, and you're in your own safe space at home. For clients juggling busy lives on tight schedules, online sessions often provide the consistency needed for successful weight management without the pressure of travel time and costs.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy promise weight loss results?
                </h3>
                <p className="text-gray-700">
                  No, I don't make promises about specific weight loss numbers, and I don't claim hypnotherapy is a magic solution. What I do promise is professional, evidence-based support to address the emotional and psychological drivers of overeating. When you understand why you eat when you're not hungry, when stress and difficult emotions no longer automatically trigger eating, when you develop healthier coping strategies for economic pressure and work stress, weight management becomes a natural consequence of feeling better. This approach creates sustainable change rather than the temporary results and inevitable rebound that diets deliver.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does working-class stress affect eating patterns?
                </h3>
                <p className="text-gray-700">
                  Economic pressure, job insecurity, shift work fatigue, and the accumulated stress of managing on tight budgets all contribute significantly to emotional eating patterns. When you're dealing with financial anxiety, food becomes one of the few affordable sources of comfort. When you're exhausted from physically demanding work or customer-facing stress, your brain craves quick energy from sugar and carbs. When healthy food costs more than processed convenience food and you don't have time or energy to cook, weight gain becomes almost inevitable. These aren't personal failures - they're predictable responses to structural challenges. Hypnotherapy helps by addressing the stress at its root and developing coping strategies that work within your actual life constraints.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What's the cost of weight management hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  You can book a single session or the 3-session package. The package provides better value and ensures commitment to the process - sustainable behavior change rarely happens in just one session. For working families in Lurgan, I understand affordability matters. There are no hidden costs, no pressure to buy additional sessions, and no expensive meal plans or supplements to purchase. What you pay is what you get - professional hypnotherapy support to address emotional eating and create lasting change in your relationship with food.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is hypnotherapy safe to use alongside diet programs?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is entirely safe to use alongside any diet program, exercise routine, or medical weight management approach. However, my philosophy differs from traditional dieting. Rather than focusing on food rules and restriction, I work on the psychological and emotional drivers of overeating. Many clients find that once they address why they eat when not hungry, they no longer need rigid diet plans - healthier eating becomes natural rather than forced. If you're working with a nutritionist or following a specific eating plan, hypnotherapy can support your efforts by reducing emotional eating, managing stress, and strengthening your commitment. Always inform all your healthcare providers about any treatments you're receiving.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default LurganWeightManagement;
